import { BonePose, clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * La Plañidera (models/mourner.ts). Idle and walk loop; attack rakes with the free claw (the blow lands at
 * ~0.13 s, when the simulation deals it); hit; stun (loop); die (she wails, crumples into her skirts and pitches
 * onto the heap; the light goes out); and her power, `heal`: the censer goes up, the fire swells and its threads
 * reach out (the `flare` bone blooms).
 *
 * Conventions as in human.ts: a negative X swings an arm forward and up; +X on a trunk leans it forward; the figure
 * faces +Z and its `R` limbs are on +X. The censer hangs plumb from the fist and swings late, the mantilla and the
 * train trail their chains (see `trail`).
 */

type B = 'root' | 'body' | 'robe' | 'train' | 'chest' | 'head' | 'veil' | 'armL' | 'foreL' | 'censer' | 'flare' | 'armR' | 'foreR';
type P = KeyPose<B>;
type Pose = (b: Bones<B>, t: number) => void;

/** A pose sheet that makes any bone it is asked for (for sampling a pose at another time). */
const scratch = (): Bones<B> => {
  const o: Record<string, BonePose> = {};
  return new Proxy(o, { get: (_, k: string) => (o[k] ??= new BonePose()) }) as Bones<B>;
};

/** A bone that trails its chain: `hang` cancels that much of the chain's tilt (1 hangs plumb), `k` swings it against the motion. */
interface Trail {
  bone: B;
  chain: readonly B[];
  lag: number;
  k: number;
  hang: number;
}

const TRAILS: readonly Trail[] = [
  { bone: 'censer', chain: ['root', 'body', 'chest', 'armL', 'foreL'], lag: 0.1, k: 1.4, hang: 1 },
  { bone: 'veil', chain: ['root', 'body', 'chest', 'head'], lag: 0.1, k: 0.45, hang: 0.5 },
  { bone: 'train', chain: ['root', 'body', 'robe'], lag: 0.12, k: 0.7, hang: 0.3 },
];

/**
 * Secondary motion: samples the pose a beat (and two) earlier and swings each trailing bone against how its chain
 * moved in between, overshooting once. Deterministic: it only re-reads the pose itself.
 */
const trail = (pose: Pose, dur: number, loop: boolean): Pose => (b, t) => {
  pose(b, t);
  const wrap = (x: number): number => (loop ? ((x % dur) + dur) % dur : Math.max(0, x));
  const at = (x: number): Bones<B> => {
    const s = scratch();
    pose(s, wrap(x));
    return s;
  };
  for (const f of TRAILS) {
    const s1 = at(t - f.lag), s2 = at(t - 2 * f.lag);
    const sum = (bb: Bones<B>, ax: 'rx' | 'rz'): number => f.chain.reduce((a, n) => a + bb[n][ax], 0);
    for (const ax of ['rx', 'rz'] as const) {
      const c0 = sum(b, ax), c1 = sum(s1, ax), c2 = sum(s2, ax);
      b[f.bone][ax] += -f.hang * c0 - f.k * (c0 - c1) + 0.55 * f.k * (c1 - c2);
    }
  }
};

const mclip = (name: string, dur: number, loop: boolean, pose: Pose, squash?: (t: number) => [number, number, number], label?: string): Clip<B> =>
  clip<B>(name, dur, loop, trail(pose, dur, loop), squash, label);

/** Hunched in grief, the censer held up, the claw hanging. */
const ST: P = { chest: [0.06, 0, 0], head: [0.08, 0, 0] };

const idle = (): Clip<B> =>
  mclip('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    // Slow sobbing breaths; the head shakes, slowly, no; the censer arm sways as if keeping a dirge.
    b.root.pos(0, -0.006 * (1 + br), 0);
    b.chest.rot(0.03 * br + 0.02 * Math.max(0, Math.sin(p * 4)) ** 4, 0.04 * Math.sin(p), 0.02 * Math.sin(p + 0.4));
    b.head.rot(-0.03 * br + 0.05 * Math.sin(p - 0.5), 0.16 * Math.sin(p + 0.3), 0.08 * Math.sin(p - 0.2));
    b.armL.rot(0.06 * Math.sin(p), 0, 0.05 * Math.sin(p + 1.2));
    b.foreL.rot(0.04 * Math.sin(p - 0.6));
    b.armR.rot(0.05 * Math.sin(p + 2), 0, 0.03 * Math.sin(p * 2));
    b.foreR.rot(-0.12 * Math.max(0, Math.sin(p * 2 + 1)) ** 2);
    b.robe.rot(0.015 * Math.sin(p - 1), 0.03 * Math.sin(p), 0.02 * Math.sin(p - 1.4));
    // The fire breathes: its threads stir and curl round.
    b.flare.rot(0, p, 0).scale(1 + 0.12 * Math.sin(p * 2 - 0.8));
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.008 * br, 1 + 0.008 * br];
  });

/** A slow glide inside her skirts: the hem rocks side to side, the train drags, the censer swings on its chains. */
const walk = (dur = 1.3): Clip<B> =>
  mclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), c2 = Math.cos(2 * th);
    apply(b, ST);
    b.root.pos(0, 0.018 * c2 - 0.008, 0).rot(0, 0, 0.03 * sn);
    b.body.rot(0.05, 0.07 * sn, 0);
    b.robe.rot(0.06 + 0.03 * c2, 0.06 * sn, 0.07 * Math.sin(th - 0.5));
    // The chest and head hold steady over the rocking, leaning into the way.
    b.chest.rot(0.06 + 0.025 * Math.cos(2 * th - 0.7), -0.06 * sn, -0.03 * sn);
    b.head.rot(-0.04 - 0.03 * Math.cos(2 * th - 1.1), -0.02 * sn, -0.03 * sn);
    b.armL.rot(-0.05 + 0.04 * Math.cos(2 * th - 0.9), 0, 0.03 * sn);
    b.armR.rot(0.16 * Math.sin(th - 0.3), 0, 0.04);
    b.foreR.rot(-0.1 * Math.max(0, -Math.sin(th - 0.8)));
    b.flare.rot(0, th, 0);
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 2);
    return [1 - 0.008 * k, 1 + 0.01 * k, 1 - 0.008 * k];
  });

/** The claw drawn back over the shoulder, then raked down across the foe (lands at 0.13 s), the body behind it. */
const UP: P = { body: [-0.05, -0.1, 0], chest: [-0.12, -0.3, 0], head: [-0.05, 0.15, 0], armR: [-2.1, 0, 0.45], foreR: [-0.7, 0, 0], armL: [0.12, 0, 0], root: [0, 0, 0, 0, 0.02, -0.03] };
const RAKE: P = { body: [0.12, 0.12, 0], chest: [0.42, 0.32, 0.05], head: [0.3, -0.12, 0], armR: [0.25, 0, -0.35], foreR: [-0.15, 0, 0], armL: [-0.08, 0, 0], robe: [0.06, 0, 0], root: [0, 0, 0, 0, -0.02, 0.09] };

const attack = (): Clip<B> =>
  mclip('attack', 0.75, false, (b, t) => {
    timeline(b, t, [
      [0, ST],
      [0.075, UP, outQuad],
      [0.13, RAKE, inCubic],
      [0.3, { ...RAKE, chest: [0.38, 0.28, 0.05], armR: [0.18, 0, -0.3] }, outBack],
      [0.75, ST, inOut],
    ], { head: 0.02, foreR: 0.025 });
    if (t > 0.13) {
      const k = settle(t - 0.13, 5, 11);
      b.armR.rot(-0.08 * k);
      b.chest.rot(0.04 * k);
    }
  }, (t) => {
    const k = t > 0.13 ? Math.max(0, settle(t - 0.13, 3, 10)) : 0;
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  });

const hit = (): Clip<B> =>
  mclip('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.12 * k, 0.08 * k);
    b.chest.rot(-0.25 * k, 0.12 * k, 0.05 * k);
    b.head.rot(-0.35 * k, -0.15 * k);
    b.armR.rot(-0.3 * k, 0, 0.15 * k);
    b.armL.rot(0.12 * k);
    b.root.pos(0, 0, -0.06 * k);
    b.flare.scale(1 - 0.3 * Math.abs(k));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1 + 0.05 * k];
  });

/** Dazed: she reels round in her skirts, the censer sagging and swinging, the head lolling. */
const stun = (): Clip<B> =>
  mclip('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    apply(b, ST, 0.5);
    b.root.rot(0.04 * Math.sin(p), 0, 0.08 * Math.cos(p));
    b.robe.rot(0, 0, -0.05 * Math.cos(p - 0.6));
    b.chest.rot(0.3 + 0.06 * Math.sin(p * 2), 0, -0.08 * Math.cos(p));
    b.head.rot(0.3 + 0.2 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.armL.rot(0.75 + 0.08 * Math.sin(p + 1), 0, 0.1);
    b.foreL.rot(0.3);
    b.armR.rot(0.25 + 0.1 * Math.sin(p + 1.2), 0, -0.05);
    b.foreR.rot(0.1);
    b.flare.scale(0.6 + 0.15 * Math.sin(p * 2));
  });

/** She wails, arms flung up; then crumples down into her skirts and pitches forward over the heap; the fire dies. */
const die = (): Clip<B> =>
  mclip('die', 1.7, false, (b, t) => {
    const heap: P = { root: [0, 0, 0, 0, -0.46, 0.04], robe: [0, 0, 0, 0, 0, 0, 0.5], body: [0.2, 0, 0.05], chest: [1.25, 0.15, 0.1], head: [0.4, 0.5, 0.2], armL: [1.4, 0, 0.5], foreL: [0.2, 0, 0], armR: [-1.0, 0, -0.5], foreR: [0, 0, 0], flare: [0, 0, 0, 0, 0, 0, 0.05] };
    timeline(b, t, [
      [0, ST],
      [0.22, { body: [-0.15, 0, 0], chest: [-0.45, 0, 0], head: [-0.6, 0, 0], armL: [-0.3, 0, -0.2], foreL: [0.4, 0, 0], armR: [-1.9, 0, 0.6], foreR: [-0.3, 0, 0], root: [0, 0, 0, 0, 0.05, 0], flare: [0, 0, 0, 0, 0, 0, 1.6] }, outExpo],
      [0.55, { body: [-0.12, 0, 0], chest: [-0.4, 0, 0], head: [-0.55, 0.1, 0], armL: [-0.28, 0, -0.2], foreL: [0.4, 0, 0], armR: [-1.8, 0, 0.6], foreR: [-0.35, 0, 0], root: [0, 0, 0, 0, 0.05, 0], flare: [0, 0, 0, 0, 0, 0, 1.2] }],
      [1.0, { root: [0, 0, 0, 0, -0.4, 0.02], robe: [0, 0, 0, 0, 0, 0, 0.56], body: [0.1, 0, 0], chest: [0.5, 0.1, 0.05], head: [0.5, 0.3, 0.1], armL: [0.6, 0, 0.3], foreL: [0.2, 0, 0], armR: [-0.3, 0, -0.2], flare: [0, 0, 0, 0, 0, 0, 0.3] }, inQuad],
      [1.4, heap, inQuad],
      [1.7, { ...heap, chest: [1.3, 0.15, 0.1] }, outBack],
    ], { head: 0.04, armR: 0.03, armL: 0.03 });
    const tr = envelope(t, 0.2, 0.28, 0.5, 0.6) * Math.sin(t * 70);
    b.chest.rot(0, 0.03 * tr, 0.02 * tr);
    b.head.rot(0.04 * tr);
  }, (t) => {
    const k = t > 1.4 ? Math.max(0, settle(t - 1.4, 3, 9)) : 0;
    return [1 + 0.06 * k, 1 - 0.07 * k, 1 + 0.06 * k];
  });

/** The healing: she lifts the censer high, head thrown back, the claw spread over her kin; the fire swells and its threads reach out. */
const heal = (): Clip<B> =>
  mclip('heal', 1.1, false, (b, t) => {
    const up: P = { body: [-0.05, 0, 0], robe: [-0.03, 0, 0], chest: [-0.26, 0, 0], head: [-0.45, -0.1, 0], armL: [-0.55, 0, 0.38], foreL: [0.35, 0, 0.15], armR: [-0.7, 0, 0.75], foreR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.04, 0] };
    timeline(b, t, [
      [0, ST],
      [0.12, { ...ST, chest: [0.2, -0.05, 0], head: [0.2, 0, 0], armL: [0.15, 0, 0], armR: [0.15, 0, 0], root: [0, 0, 0, 0, -0.02, 0] }, outQuad],
      [0.36, up, outBack],
      [0.78, { ...up, chest: [-0.23, 0, 0], head: [-0.4, -0.08, 0], armL: [-0.52, 0, 0.35] }],
      [1.1, ST, inOut],
    ], { head: 0.03, foreR: 0.03 });
    // The fire swells past full, holds trembling, and draws back in.
    const k = t < 0.3 ? 1 - 0.3 * span(t, 0, 0.12) : t < 0.5 ? 0.7 + 3.3 * span(t, 0.3, 0.5, outBack) : 4 - 3 * span(t, 0.62, 1.05, inOut);
    b.flare.rot(0, 5 * t, 0).scale(k);
    const tr = envelope(t, 0.36, 0.42, 0.7, 0.8) * Math.sin(t * 60);
    b.armL.rot(0.02 * tr, 0, 0.02 * tr);
    b.armR.rot(0, 0, 0.04 * tr);
  }, (t) => {
    const k = envelope(t, 0.25, 0.4, 0.75, 1.05, outCubic);
    return [1 - 0.015 * k, 1 + 0.03 * k, 1 - 0.015 * k];
  }, 'Curar');

/** Her clips (see models/mourner.ts for the bones). */
export const mournerClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), heal()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { mourner: mournerClips };
